import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Grid from '@mui/system/Unstable_Grid';
import { Stack, Button } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useGetContactDocumentsQuery, useRemoveContactDocumentMutation } from 'src/services/contacts/contacts.service';

import { useTable } from 'src/components/table';
import FileManagerTable from 'src/components/documents/file-manager-table';

import ContactDocumentUploadModal from '../modal/contact-document-uploader';

const DEFAULT_PAGE = {
  page: 1,
  per_page: 10,
};

type Props = {
  contactId?: string;
  uploadVisible?: boolean;
};

export default function ContactDocumentsView({ contactId, uploadVisible = true }: Props) {
  const [page, setPage] = useState(DEFAULT_PAGE);

  const showUploadModal = useBoolean();

  const { t } = useTranslate();

  const [removeDocument, { isLoading: isRemoving }] = useRemoveContactDocumentMutation();

  const { currentData: contactDocuments, isLoading } = useGetContactDocumentsQuery(
    {
      contact_id: contactId as string,
      params: page,
    },
    { skip: !contactId }
  );

  const table = useTable();

  const notFound = !contactDocuments?.count && !isLoading;

  const onAddSuccess = () => {
    showUploadModal.onFalse();
  };

  const handleDeleteItem = async (id: string) => {
    try {
      await removeDocument({
        document_id: id,
        contact_id: contactId as string
      }).unwrap();

      enqueueSnackbar(t('contacts.documents.delete_success'), { variant: 'success' });

    } catch (error) {
      enqueueSnackbar(t('contacts.documents.delete_error'), { variant: 'error' });
    }
  };

  return (
    <Grid container spacing={3}>
      {
        uploadVisible && (
          <Grid xs={12}>
            <Stack direction="row" justifyContent="flex-end" mt={2}>
              <Button variant="contained" disabled={!contactId} onClick={showUploadModal.onTrue}>
                {t('contacts.documents.upload')}
              </Button>
            </Stack>
          </Grid>
        )
      }
      <Grid xs={12}>
        <Card>
          <FileManagerTable
            table={table}
            dataFiltered={contactDocuments?.results ?? []}
            count={contactDocuments?.count ?? 0}
            onDeleteRow={handleDeleteItem}
            notFound={notFound}
            documentType="company_profile"
            onChangePage={(_e, pageNumber) => setPage({ ...page, page: pageNumber + 1 })}
            onChangePerPage={(e) => setPage({ ...page, per_page: Number(e.target.value) })}
            pageOptions={page}
          />
        </Card>
      </Grid>

      {
        contactId && (
          <ContactDocumentUploadModal
            open={showUploadModal.value}
            onClose={showUploadModal.onFalse}
            contactId={contactId}
            onUpload={onAddSuccess}
          />
        )
      }
    </Grid>
  );
}